import type { PerfOverlay, PerfSnapshot } from '../systems/PerfOverlay';

export class Loop {
  private frameId = 0;
  private lastTime = 0;
  private running = false;

  constructor(
    private readonly update: (deltaSeconds: number, elapsedSeconds: number) => void,
    private readonly render: () => void,
    private readonly perf?: PerfOverlay,
    private readonly snapshot?: () => PerfSnapshot | undefined,
  ) {}

  start(): void {
    if (this.running) return;
    this.running = true;
    this.lastTime = performance.now();
    this.frameId = requestAnimationFrame(this.tick);
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.frameId);
  }

  private readonly tick = (time: number) => {
    if (!this.running) return;
    // The raw delta (unclamped) is what the perf overlay needs to see real
    // hitches; the sim still gets the clamped delta to avoid physics pops.
    const rawDeltaSeconds = (time - this.lastTime) / 1000;
    this.lastTime = time;
    const deltaSeconds = Math.min(rawDeltaSeconds, 0.05);
    const perf = this.perf;
    if (perf && perf.enabled) {
      perf.beginFrame(rawDeltaSeconds);
      this.update(deltaSeconds, time / 1000);
      perf.mark('update');
      this.render();
      perf.mark('render');
      perf.endFrame(this.snapshot?.());
    } else {
      this.update(deltaSeconds, time / 1000);
      this.render();
    }
    this.frameId = requestAnimationFrame(this.tick);
  };
}
